import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Phone, ArrowRight, ShieldCheck, Users, TrendingUp } from "lucide-react";
import { requestPartnerOtp } from "../../api/partner/partnerApi";
import { setPartnerMobile, setPartnerError, setPartnerLoading } from "../../features/partnerOnboarding/partnerOnboardingSlice";
import { showToast } from "../../components/common/notification/Notification";
import PartnerLayout from "../../components/partner/PartnerLayout";
import HeroImg from "../../assets/images/hero-1.jpg";
import "./PartnerPages.css";

const WhatsAppIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"
      fill="#25D366"
    />
    <path
      d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.532 5.857L0 24l6.306-1.508A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.797 9.797 0 01-5.003-1.373l-.36-.214-3.737.893.938-3.63-.235-.374A9.772 9.772 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z"
      fill="#25D366"
    />
  </svg>
);

export const PartnerSignup = () => {
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleMobileChange = (e) => {
    const numeric = e.target.value.replace(/\D/g, "");
    if (numeric.length <= 10) {
      setMobile(numeric);
    }
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    if (mobile.length !== 10) {
      showToast("Please enter a valid 10-digit mobile number.", { type: "error" });
      return;
    }

    setLoading(true);
    dispatch(setPartnerLoading(true));

    try {
      const response = await requestPartnerOtp(mobile, "register");
      if (response?.statusType === "SUCCESS" || response?.status === "SUCCESS") {
        dispatch(setPartnerMobile(mobile));
        showToast("OTP sent successfully to your mobile number!", { type: "success" });
        navigate("/partner/verify-otp", { state: { mobile } });
      } else {
        const errorMsg = response?.text || response?.message || "Failed to send OTP. Please try again.";
        showToast(errorMsg, { type: "error" });
        dispatch(setPartnerError(errorMsg));
      }
    } catch (err) {
      console.error("Partner OTP request error:", err);
      const msg = err?.response?.data?.text || err?.response?.data?.message || "Unable to send OTP. Please try again later.";
      showToast(msg, { type: "error" });
      dispatch(setPartnerError(msg));
    } finally {
      setLoading(false);
      dispatch(setPartnerLoading(false));
    }
  };

  return (
    <PartnerLayout
      currentStepIndex={1}
      title="Become an AHD Partner"
      subtitle="Join India's leading real estate and home services ecosystem to grow your business."
      visualImage={HeroImg}
      visualTitle="Grow your business with AHD"
      visualSubtitle="Connect with customers, showcase your services, and grow your presence across the AHD ecosystem."
      visualHighlights={[
        {
          icon: Users,
          title: "Verified Customer Leads",
          desc: "Direct inquiries from verified buyers, tenants, and homeowners",
        },
        {
          icon: TrendingUp,
          title: "Higher Business Visibility",
          desc: "Showcase your catalog and listings with maximum search prominence",
        },
        {
          icon: ShieldCheck,
          title: "Trusted Partner Profile",
          desc: "Build instant credibility with official AHD partner verification badge",
        },
      ]}
    >
      <div className="partner-form-container">
        <form onSubmit={handleContinue} className="partner-form">
          <div className="partner-form__group">
            <label className="partner-form__label" htmlFor="partner-mobile">
              Mobile Number <span className="partner-required">*</span>
            </label>
            <div className="partner-input-wrap">
              <span className="partner-input-icon">
                <Phone size={18} />
              </span>
              <span className="partner-input-divider" />
              <span className="partner-input-prefix">+91</span>
              <input
                id="partner-mobile"
                type="tel"
                inputMode="numeric"
                placeholder="Enter 10-digit number"
                className="partner-input"
                value={mobile}
                onChange={handleMobileChange}
                maxLength={10}
                autoFocus
              />
            </div>
            <div className="partner-whatsapp-note">
              <WhatsAppIcon />
              <span>OTP verification code will be sent via SMS / WhatsApp</span>
            </div>
          </div>

          <button
            type="submit"
            className="partner-btn partner-btn--primary partner-btn--lg"
            disabled={mobile.length !== 10 || loading}
          >
            {loading ? (
              <span className="partner-spinner">Sending OTP...</span>
            ) : (
              <>
                <span>Continue</span>
                <ArrowRight size={18} />
              </>
            )}
          </button>

          <div className="partner-auth-switch">
            <span>Already registered?</span>
            <Link to="/login" className="partner-auth-switch__link">
              Sign In
            </Link>
          </div>
        </form>
      </div>
    </PartnerLayout>
  );
};

export default PartnerSignup;
